import {
  StyleSheet, css 
} from "aphrodite"
import React, {
  Fragment, useCallback, useEffect, useState 
} from "react"
import ScrollLock from "react-scrolllock"
import Arrow from "./components/Arrow"
import Container from "./components/Container"
import Footer from "./components/Footer"
import Header from "./components/Header"
import PaginatedThumbnails from "./components/PaginatedThumbnails"
import DefaultSpinner from "./components/Spinner"
import defaultTheme from "./theme"

import {
  canUseDom, deepMerge 
} from "./utils/util"

function normalizeSourceSet(data) {
  const sourceSet = data?.srcSet || data?.srcset

  if (Array.isArray(sourceSet)) {
    return sourceSet.join()
  }

  return sourceSet
}

const ThemeContext = React.createContext({
  theme: defaultTheme,
  toggleTheme: (newTheme) => {},
})

const ImgsViewer = (props) => {
  const {
    isOpen,
    currImg = 0,
    enableKeyboardInput = true,
    imgCountSeparator = " / ",
    imgs,
    preloadNextImg = true,
    preventScroll = true,
    showCloseBtn = true,
    showImgCount = true,
    spinnerDisabled = false,
    spinner = DefaultSpinner,
    spinnerColor = "#fff",
    spinnerSize = 50,
    theme: propsTheme = {},
    thumbnailOffset = 2,
    width = 1024,
    showThumbnails,
    backdropCloseable,
    onClickImg,
    customControls,
    onClose,
    closeBtnTitle,
    onClickNext,
    onClickPrev,
  } = props

  const [imgLoaded, setImgLoaded] = useState(false)

  const [theme, setTheme] = useState(deepMerge(defaultTheme, propsTheme))

  const classes = StyleSheet.create(deepMerge(defaultStyles, propsTheme))

  const toggleTheme = (theme) => {
    setTheme(theme)
  }

  const preloadImgData = useCallback((data, onload) => {
    if (!data) { return }

    const img = new Image()
    const sourceSet = normalizeSourceSet(data)

    // Todo: add error handling for missing imgs
    img.onerror = onload
    img.onload = onload
    img.src = data.src

    if (sourceSet) { img.srcset = sourceSet }

    return img
  }, [])

  const preloadImg = useCallback(
    (idx, onload) => {
      return preloadImgData(imgs[idx], onload)
    },
    [imgs, preloadImgData]
  )

  const gotoNext = useCallback(
    (event) => {
      if (!imgLoaded || currImg === imgs.length - 1) { return }

      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }

      onClickNext()
    },
    [currImg, imgLoaded, imgs.length, onClickNext]
  )

  const gotoPrev = useCallback(
    (event) => {
      if (!imgLoaded || currImg === 0) { return }

      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }

      onClickPrev()
    },
    [currImg, imgLoaded, onClickPrev]
  )

  const closeBackdrop = useCallback(
    (event) => {
      if (
        event.target.id === "viewerBackdrop" ||
                event.target.tagName === "FIGURE"
      ) {
        onClose()
      }
    },
    [onClose]
  )

  const handleKeyboardInput = useCallback(
    (event) => {
      const {
        keyCode 
      } = event

      if (keyCode === 37 || keyCode === 33 || keyCode === 38) {
        // left, pageup, up
        gotoPrev(event)

        return true
      } else if (keyCode === 39 || keyCode === 34 || keyCode === 40) {
        // right, pagedown, down
        gotoNext(event)

        return true
      } else if (keyCode === 27 || keyCode === 32) {
        // esc, space
        onClose()

        return true
      }

      return false
    },
    [gotoNext, gotoPrev, onClose]
  )

  const handleImgLoaded = () => {
    setImgLoaded(true)
  }

  useEffect(() => {
    if (isOpen) {
      if (enableKeyboardInput) {
        window.addEventListener("keydown", handleKeyboardInput)
      }

      if (typeof currImg === "number") {
        preloadImg(currImg, handleImgLoaded)
      }
    }

    return () => {
      if (enableKeyboardInput) {
        window.removeEventListener("keydown", handleKeyboardInput)
      }
    }
  })

  useEffect(() => {
    if (!canUseDom) { return }

    if (preloadNextImg) {
      const nextIdx = currImg + 1
      const prevIdx = currImg - 1

      preloadImg(prevIdx)
      preloadImg(nextIdx)
    }

    // preload currImg
    const img = preloadImgData(imgs[currImg], handleImgLoaded)

    if (img) { setImgLoaded(img.complete) }
  }, [currImg, imgs, preloadImg, preloadImgData, preloadNextImg])

  const renderArrowPrev = (theme) => {
    if (currImg === 0) { return null }

    return (
      <Arrow
        theme={theme}
        direction='left'
        icon='arrowLeft'
        onClick={gotoPrev}
        type='button'
      />
    )
  }

  const renderArrowNext = (theme) => {
    if (currImg === imgs.length - 1) { return null }

    return (
      <Arrow
        theme={theme}
        direction='right'
        icon='arrowRight'
        onClick={gotoNext}
        type='button'
      />
    )
  }

  const renderDialog = (newState) => {
    if (!isOpen) { return <span key='closed' /> }

    const offsetThumbnails = showThumbnails
      ? theme.thumbnail.size + theme.container.gutter.vertical
      : 0

    return (
      <ThemeContext.Consumer>
        {({
          theme, toggleTheme 
        }) => {
          theme = newState.theme

          return (
            <Container
              theme={theme}
              key='open'
              onClick={backdropCloseable && closeBackdrop}
              onTouchEnd={backdropCloseable && closeBackdrop}
            >
              {imgLoaded && renderHeader(theme)}
              <Fragment>
                <div
                  className={css(classes.content)}
                  style={{
                    marginBottom: offsetThumbnails,
                    maxWidth: width,
                  }}
                >
                  {renderImgs(theme)}
                  {renderSpinner()}
                  {imgLoaded && renderFooter(theme)}
                  {imgLoaded && renderArrowPrev(theme)}
                  {imgLoaded && renderArrowNext(theme)}
                </div>
                {imgLoaded && renderThumbnails(theme)}

                {preventScroll && <ScrollLock />}
              </Fragment>
            </Container>
          )
        }}
      </ThemeContext.Consumer>
    )
  }

  const renderImgs = (theme) => {
    if (!imgs || !imgs.length) { return null }

    const img = imgs[currImg]
    const sourceSet = normalizeSourceSet(img)
    const sizes = sourceSet ? "100vw" : null

    const thumbnailsSize = showThumbnails ? theme.thumbnail.size : 0
    const heightOffset = `${
      theme.header.height +
            theme.footer.height +
            thumbnailsSize +
            theme.container.gutter.vertical
    }px`

    return (
      <figure className={css(classes.figure)}>
        <img
          className={css(classes.img, imgLoaded && classes.imgLoaded)}
          onClick={onClickImg}
          sizes={sizes}
          alt={img.alt}
          src={img.src}
          srcSet={sourceSet}
          style={{
            cursor: onClickImg ? "pointer" : "auto",
            maxHeight: `calc(100vh - ${heightOffset}`,
          }}
        />
      </figure>
    )
  }

  const renderThumbnails = (theme) => {
    if (!showThumbnails) { return null }

    return (
      <PaginatedThumbnails
        theme={theme}
        currImg={currImg}
        imgs={imgs}
        offset={thumbnailOffset}
        onClickThumbnail={onClickThumbnail}
      />
    )
  }

  const renderHeader = (theme) => {
    return (
      <Header
        theme={theme}
        customControls={customControls}
        onClose={onClose}
        showCloseBtn={showCloseBtn}
        closeBtnTitle={closeBtnTitle}
        count={imgs.length}
        currentIndex={currImg}
      />
    )
  }

  const renderFooter = (theme) => {
    if (!imgs || !imgs.length) { return null }

    return (
      <Footer
        theme={theme}
        caption={imgs[currImg].caption}
        countCurr={currImg + 1}
        countSeparator={imgCountSeparator}
        countTotal={imgs.length}
        showCount={showImgCount}
      />
    )
  }

  const renderSpinner = () => {
    const Spinner = spinner

    if (spinnerDisabled) { return null }

    return (
      <div
        className={css(
          classes.spinner,
          !imgLoaded && classes.spinnerActive
        )}
      >
        <Spinner color={spinnerColor} size={spinnerSize} />
      </div>
    )
  }

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
      }}
    >
      <>
        {renderDialog({
          imgLoaded,
          theme,
          toggleTheme,
        })}
      </>
    </ThemeContext.Provider>
  )
}

const defaultStyles = {
  content: {
    position: "relative",
    height: "100%",
  },
  figure: {
    margin: 0,
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  img: {
    display: "block", // removes browser default gutter
    height: "auto",
    margin: "0 auto", // main center on very short screens or very narrow img
    maxWidth: "100%",

    // disable user select
    WebkitTouchCallout: "none",
    userSelect: "none",

    // opacity animation on image load
    opacity: 0,
    transition: "opacity .3s",
  },
  imgLoaded: {
    opacity: 1,
  },
  spinner: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",

    // opacity animation to make spinner appear with delay
    opacity: 0,
    transition: "opacity .3s",
    pointerEvents: "none",
  },
  spinnerActive: {
    opacity: 1,
  },
}

export default ImgsViewer
